/**
 * Queues Management Page
 * 
 * Configure which organizational units act as queues for item submissions.
 * Each org unit in the hierarchy can be enabled as a queue.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useWorkspaceRoster } from '@/features/cycle-designer/hooks/useWorkspaceRoster';
import { 
  Inbox,
  ChevronRight,
  ChevronDown,
  Building2,
  Plus,
  Users,
  Briefcase,
  FolderTree,
  Settings,
  Check,
  X,
  AlertCircle,
  Code,
  Copy,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import type { OrgUnit, OrgUnitType, QueueSettings } from '../../../types/prioritization/enterpriseIdentity';
import { getCurrentWorkspaceId, getSafeSupabaseClient } from '../../../lib/supabaseSync';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { isModuleEnabled, useWorkspaceModules } from '../../../lib/workspaceModulesService';
import { listEmbedTokens, createEmbedToken, updateEmbedTokenSource, type IntakeEmbedToken } from '../../../lib/intakeEmbedTokens';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { useItemSourcesStore } from '../../../stores/prioritization/itemSourcesStore';

// ============================================================================
// Types
// ============================================================================

interface OrgUnitTreeNode extends OrgUnit {
  children: OrgUnitTreeNode[];
}

// ============================================================================
// Helper Functions
// ============================================================================

function getOrgUnitIcon(type: OrgUnitType) {
  switch (type) {
    case 'organization':
      return Building2;
    case 'division':
      return Briefcase;
    case 'department':
      return FolderTree;
    case 'team':
      return Users;
    case 'workgroup':
      return Users;
    case 'project':
      return Inbox;
    default:
      return FolderTree;
  }
}

function buildOrgTree(orgUnits: OrgUnit[]): OrgUnitTreeNode[] {
  const nodeMap = new Map<string, OrgUnitTreeNode>();
  const roots: OrgUnitTreeNode[] = [];
  
  // Create nodes
  orgUnits.forEach(unit => {
    nodeMap.set(unit.id, { ...unit, children: [] });
  });
  
  // Build tree
  orgUnits.forEach(unit => {
    const node = nodeMap.get(unit.id)!;
    if (unit.parentId && nodeMap.has(unit.parentId)) {
      nodeMap.get(unit.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  });
  
  // Sort children by name
  const sortChildren = (nodes: OrgUnitTreeNode[]) => {
    nodes.sort((a, b) => a.displayName.localeCompare(b.displayName));
    nodes.forEach(node => sortChildren(node.children));
  };
  sortChildren(roots);
  
  return roots;
}

// ============================================================================
// Components
// ============================================================================

interface QueueSettingsEditorProps {
  orgUnit: OrgUnit;
  onSave: (settings: QueueSettings) => void;
  onClose: () => void;
}

interface OrgUnitCreatorProps {
  workspaceId: string | null;
  orgUnits: OrgUnit[];
  onCreate: (unit: Omit<OrgUnit, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onClose: () => void;
}

function QueueSettingsEditor({ orgUnit, onSave, onClose }: QueueSettingsEditorProps) {
  const roster = useWorkspaceRoster(true);
  const [settings, setSettings] = useState<QueueSettings>(
    orgUnit.queueSettings || {
      enabled: false,
      acceptsSubmissions: true,
      requiresApproval: false,
      description: '',
    }
  );
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[var(--text-pri)]">
            Queue Settings: {orgUnit.displayName}
          </h3>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          {/* Enable Queue */}
          <label className="flex items-center justify-between p-3 bg-[var(--surface)] cursor-pointer">
            <div>
              <div className="font-medium text-[var(--text-pri)]">Enable as Queue</div>
              <div className="text-sm text-[var(--text-ter)]">Allow items to be routed to this org unit</div>
            </div>
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
              className="h-5 w-5 text-primary-600 rounded"
            />
          </label>
          
          {settings.enabled && (
            <>
              {/* Accepts Submissions */}
              <label className="flex items-center justify-between p-3 bg-[var(--surface)] cursor-pointer">
                <div>
                  <div className="font-medium text-[var(--text-pri)]">Accept Direct / Embedded Submissions</div>
                  <div className="text-sm text-[var(--text-ter)]">Allow intake links or embedded forms to send submissions directly to this queue</div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.acceptsSubmissions}
                  onChange={(e) => setSettings({ ...settings, acceptsSubmissions: e.target.checked })}
                  className="h-5 w-5 text-primary-600 rounded"
                />
              </label>
              
              {/* Requires Approval */}
              <label className="flex items-center justify-between p-3 bg-[var(--surface)] cursor-pointer">
                <div>
                  <div className="font-medium text-[var(--text-pri)]">Require Approval</div>
                  <div className="text-sm text-[var(--text-ter)]">Items need an Item Manager's approval before structuring</div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.requiresApproval}
                  onChange={(e) => setSettings({ ...settings, requiresApproval: e.target.checked })}
                  className="h-5 w-5 text-primary-600 rounded"
                />
              </label>
              
              {/* Default owner — never overwrites an owner the item already has. */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Default Owner
                </label>
                <select
                  value={settings.defaultStewardId ?? ''}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      defaultStewardId: e.target.value || undefined,
                      defaultOwnerApplyTo: settings.defaultOwnerApplyTo ?? 'new',
                    })
                  }
                  className="w-full px-3 py-2 border border-[var(--rule)] text-sm"
                  disabled={roster.loading}
                >
                  <option value="">— No default owner —</option>
                  {roster.users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name || u.email}
                    </option>
                  ))}
                </select>

                {settings.defaultStewardId && (
                  <div className="mt-2 space-y-1.5">
                    <label className="flex items-start gap-2 text-sm text-[var(--text-sec)] cursor-pointer">
                      <input
                        type="radio"
                        name="defaultOwnerApplyTo"
                        checked={(settings.defaultOwnerApplyTo ?? 'new') === 'new'}
                        onChange={() => setSettings({ ...settings, defaultOwnerApplyTo: 'new' })}
                        className="mt-1"
                      />
                      <span>
                        <span className="font-medium">New items only</span>
                        <span className="block text-xs text-[var(--text-ter)]">
                          Items arriving in this queue from now on.
                        </span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2 text-sm text-[var(--text-sec)] cursor-pointer">
                      <input
                        type="radio"
                        name="defaultOwnerApplyTo"
                        checked={settings.defaultOwnerApplyTo === 'new_and_existing'}
                        onChange={() =>
                          setSettings({ ...settings, defaultOwnerApplyTo: 'new_and_existing' })
                        }
                        className="mt-1"
                      />
                      <span>
                        <span className="font-medium">New and existing items</span>
                        <span className="block text-xs text-[var(--text-ter)]">
                          Also fills in items already in this queue that have no owner.
                        </span>
                      </span>
                    </label>
                    <p className="text-xs text-[var(--text-ter)] pt-1">
                      An item that already has an owner keeps it — neither option overwrites.
                    </p>
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Queue Description
                </label>
                <textarea
                  value={settings.description || ''}
                  onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                  placeholder="Describe what types of items should be submitted to this queue..."
                  rows={3}
                  className="w-full px-3 py-2 border border-[var(--rule)] text-sm"
                />
              </div>
            </>
          )}
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button onClick={() => onSave(settings)} className="btn btn-primary">
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
}

function OrgUnitCreator({ workspaceId, orgUnits, onCreate, onClose }: OrgUnitCreatorProps) {
  const [displayName, setDisplayName] = useState('');
  const [type, setType] = useState<OrgUnitType>('team');
  const [parentId, setParentId] = useState('');
  const [description, setDescription] = useState('');

  const parent = parentId ? orgUnits.find((unit) => unit.id === parentId) : undefined;
  const canCreate = displayName.trim().length > 0 && workspaceId;

  const handleCreate = () => {
    if (!workspaceId || !displayName.trim()) return;

    const name = displayName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    onCreate({
      externalId: undefined,
      name: name || displayName.trim().toLowerCase(),
      displayName: displayName.trim(),
      type,
      parentId: parent?.id,
      path: parent ? [...parent.path, `temp-${name || 'org-unit'}`] : [`temp-${name || 'org-unit'}`],
      depth: parent ? parent.depth + 1 : 0,
      workspaceId,
      description: description.trim() || undefined,
      active: true,
      queueSettings: {
        enabled: false,
        acceptsSubmissions: true,
        requiresApproval: false,
        description: '',
      },
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg bg-[var(--bg-raised)] p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[var(--text-pri)]">Add Organizational Unit</h3>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            <X className="h-5 w-5" />
          </button>
        </div>

        {!workspaceId && (
          <div className="mb-4 border border-[var(--mustard)] bg-[var(--mustard-dim)] p-3 text-sm text-[var(--mustard)]">
            Select a workspace before creating org units.
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-[var(--text-sec)]">Display Name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Product Operations"
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--text-sec)]">Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as OrgUnitType)}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm bg-[var(--bg-raised)]"
              >
                <option value="organization">Organization</option>
                <option value="division">Division</option>
                <option value="department">Department</option>
                <option value="team">Team</option>
                <option value="workgroup">Workgroup</option>
                <option value="project">Project</option>
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--text-sec)]">Parent</label>
              <select
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm bg-[var(--bg-raised)]"
              >
                <option value="">No parent (root)</option>
                {orgUnits.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.displayName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-[var(--text-sec)]">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm"
              placeholder="Optional description"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button onClick={handleCreate} disabled={!canCreate} className="btn btn-primary disabled:opacity-50">
            Create Org Unit
          </button>
        </div>
      </div>
    </div>
  );
}

interface OrgUnitRowProps {
  node: OrgUnitTreeNode;
  depth: number;
  expanded: Set<string>;
  onToggleExpand: (id: string) => void;
  onEditQueue: (unit: OrgUnit) => void;
  onOpenEmbed: (unit: OrgUnit) => void;
  showIntakeEmbed: boolean;
}

function OrgUnitRow({ node, depth, expanded, onToggleExpand, onEditQueue, onOpenEmbed, showIntakeEmbed }: OrgUnitRowProps) {
  const Icon = getOrgUnitIcon(node.type);
  const hasChildren = node.children.length > 0;
  const isExpanded = expanded.has(node.id);
  const isQueueEnabled = node.queueSettings?.enabled;
  const acceptsSubmissions = node.queueSettings?.acceptsSubmissions;
  
  return (
    <>
      <div 
        className={`flex items-center gap-2 py-2 px-3 hover:bg-[var(--surface)]  transition-colors ${
          depth > 0 ? 'ml-' + (depth * 6) : ''
        }`}
        style={{ marginLeft: depth * 24 }}
      >
        {/* Expand/Collapse Button */}
        <button
          onClick={() => onToggleExpand(node.id)}
          className={`p-1 rounded hover:bg-[var(--hover-tint)] ${hasChildren ? '' : 'invisible'}`}
          disabled={!hasChildren}
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" />
          ) : (
            <ChevronRight className="h-4 w-4 text-[var(--text-ter)]" />
          )}
        </button>
        
        {/* Icon */}
        <Icon className={`h-5 w-5 ${isQueueEnabled ? 'text-primary-600' : 'text-[var(--text-ter)]'}`} />
        
        {/* Name */}
        <div className="flex-1">
          <span className="font-medium text-[var(--text-pri)]">{node.displayName}</span>
          <span className="ml-2 text-xs text-[var(--text-ter)] capitalize">{node.type}</span>
        </div>
        
        {/* Queue Status */}
        {isQueueEnabled ? (
          <span className="flex items-center gap-1 px-2 py-1 bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] text-xs rounded-full">
            <Check className="h-3 w-3" />
            Queue Enabled
          </span>
        ) : (
          <span className="text-xs text-[var(--text-ter)]">Not a queue</span>
        )}
        
        {/* Embed code (only when queue enabled and accepts submissions) */}
        {showIntakeEmbed && isQueueEnabled && acceptsSubmissions && (
          <button
            onClick={() => onOpenEmbed(node)}
            className="flex items-center gap-1 px-2 py-1 text-sm text-primary-600 hover:bg-primary-50"
            title="Get embed code"
          >
            <Code className="h-4 w-4" />
            Embed
          </button>
        )}
        
        {/* Settings Button */}
        <button
          onClick={() => onEditQueue(node)}
          className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)]"
        >
          <Settings className="h-4 w-4" />
        </button>
      </div>
      
      {/* Children */}
      {isExpanded && node.children.map(child => (
        <OrgUnitRow
          key={child.id}
          node={child}
          depth={depth + 1}
          expanded={expanded}
          onToggleExpand={onToggleExpand}
          onEditQueue={onEditQueue}
          onOpenEmbed={onOpenEmbed}
          showIntakeEmbed={showIntakeEmbed}
        />
      ))}
    </>
  );
}

// ============================================================================
// Embed code modal
// ============================================================================

interface EmbedCodeModalProps {
  orgUnit: OrgUnit;
  onClose: () => void;
}

function EmbedCodeModal({ orgUnit, onClose }: EmbedCodeModalProps) {
  const addNotification = useUIStore((s) => s.addNotification);
  const [token, setToken] = useState<IntakeEmbedToken | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSource, setSelectedSource] = useState<string>('');
  const [savingSource, setSavingSource] = useState(false);
  const workspaceId = getCurrentWorkspaceId();

  const { sources: itemSources, fetchSources } = useItemSourcesStore();
  const activeSources = itemSources.filter((s) => s.isActive);

  useEffect(() => {
    if (workspaceId) fetchSources(workspaceId);
  }, [workspaceId, fetchSources]);

  const loadOrCreateToken = useCallback(async () => {
    if (!workspaceId) {
      setError('No workspace selected');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const { data: list } = await listEmbedTokens(workspaceId, orgUnit.id);
    if (list && list.length > 0) {
      setToken(list[0]!);
      setSelectedSource(list[0]!.defaultSource ?? '');
      setLoading(false);
      return;
    }
    const { data: created, error: createErr } = await createEmbedToken(workspaceId, orgUnit.id);
    if (createErr) {
      setError(createErr);
      setLoading(false);
      return;
    }
    setToken(created!);
    setSelectedSource(created!.defaultSource ?? '');
    setLoading(false);
  }, [workspaceId, orgUnit.id]);

  useEffect(() => {
    setToken(null);
    setLoading(true);
    setError(null);
    if (workspaceId && orgUnit.id) loadOrCreateToken();
  }, [orgUnit.id, workspaceId, loadOrCreateToken]);

  const handleSaveSource = async () => {
    if (!token) return;
    setSavingSource(true);
    const { error: saveErr } = await updateEmbedTokenSource(token.id, selectedSource || null);
    setSavingSource(false);
    if (saveErr) {
      addNotification({ type: 'error', title: 'Save failed', message: saveErr, duration: 4000 });
    } else {
      setToken({ ...token, defaultSource: selectedSource || null });
      addNotification({
        type: 'success',
        title: 'Default source saved',
        message: selectedSource
          ? `Submissions from this embed will be tagged as "${activeSources.find((s) => s.value === selectedSource)?.label ?? selectedSource}".`
          : 'No default source — submissions will use the queue name.',
        duration: 4000,
      });
    }
  };

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const embedUrl = token ? `${baseUrl}/prioritization/intake/embed?token=${token.token}` : '';
  const iframeSnippet = token
    ? `<iframe src="${embedUrl}" title="Submit an idea" width="100%" height="400" frameborder="0"></iframe>`
    : '';

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(
      () => addNotification({ type: 'success', title: 'Copied', message: `${label} copied to clipboard` }),
      () => addNotification({ type: 'error', title: 'Copy failed', message: 'Could not copy to clipboard' })
    );
  };

  const sourceChanged = token ? selectedSource !== (token.defaultSource ?? '') : false;

  if (!workspaceId) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-lg p-6">
          <p className="text-[var(--text-sec)]">Select a workspace in the sidebar to generate embed code.</p>
          <button onClick={onClose} className="mt-4 btn btn-secondary">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[var(--text-pri)]">
            Embed code: {orgUnit.displayName}
          </h3>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            <X className="h-5 w-5" />
          </button>
        </div>
        {loading && !token && (
          <p className="text-[var(--text-ter)]">Generating embed token…</p>
        )}
        {error && (
          <div className="mb-4 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-sm text-[var(--coral)]">
            {error}
          </div>
        )}
        {token && (
          <div className="space-y-4">
            <p className="text-sm text-[var(--text-sec)]">
              Place this link or iframe on your site. Submissions will go to the <strong>{orgUnit.displayName}</strong> queue.
            </p>

            {/* Default source */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Default source
                <span className="text-[var(--text-ter)] font-normal ml-1">(Optional)</span>
              </label>
              <div className="flex gap-2">
                <select
                  value={selectedSource}
                  onChange={(e) => setSelectedSource(e.target.value)}
                  className="flex-1 px-3 py-2 border border-[var(--rule)] text-sm bg-[var(--bg-raised)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">No default — use queue name</option>
                  {activeSources.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                {sourceChanged && (
                  <button
                    type="button"
                    onClick={handleSaveSource}
                    disabled={savingSource}
                    className="px-3 py-2 text-sm bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    {savingSource ? 'Saving…' : 'Save'}
                  </button>
                )}
              </div>
              <p className="text-xs text-[var(--text-ter)] mt-1">
                When set, all submissions through this embed are automatically tagged with this source.
              </p>
            </div>

            {/* Direct link */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Direct link</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={embedUrl}
                  className="flex-1 px-3 py-2 border border-[var(--rule)] text-sm font-mono bg-[var(--surface)]"
                />
                <button
                  type="button"
                  onClick={() => copyToClipboard(embedUrl, 'Link')}
                  className="p-2 border border-[var(--rule)] hover:bg-[var(--surface)]"
                  title="Copy link"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            </div>
            {/* Iframe snippet */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Embed snippet (iframe)</label>
              <div className="flex gap-2">
                <textarea
                  readOnly
                  value={iframeSnippet}
                  rows={3}
                  className="flex-1 px-3 py-2 border border-[var(--rule)] text-sm font-mono bg-[var(--surface)]"
                />
                <button
                  type="button"
                  onClick={() => copyToClipboard(iframeSnippet, 'Snippet')}
                  className="p-2 border border-[var(--rule)] hover:bg-[var(--surface)] self-start"
                  title="Copy snippet"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="btn btn-primary">Done</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function Queues() {
  const orgUnits = useEnterpriseIdentityStore((state) => state.orgUnits);
  const fetchOrgUnits = useEnterpriseIdentityStore((state) => state.fetchFromSupabase);
  const addOrgUnit = useEnterpriseIdentityStore((state) => state.addOrgUnit);
  const updateOrgUnit = useEnterpriseIdentityStore((state) => state.updateOrgUnit);
  const loadDemoData = useEnterpriseIdentityStore((state) => state.loadDemoData);
  const addNotification = useUIStore((state) => state.addNotification);
  const fetchItems = useItemsStore((state) => state.fetchFromSupabase);
  const workspaceId = getCurrentWorkspaceId();
  const workspaceFeatures = useWorkspaceModules();
  const showIntakeEmbed = isModuleEnabled(workspaceFeatures, 'intake_embed');
  
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editingUnit, setEditingUnit] = useState<OrgUnit | null>(null);
  const [embedUnit, setEmbedUnit] = useState<OrgUnit | null>(null);
  const [showCreateOrgUnit, setShowCreateOrgUnit] = useState(false);

  useEffect(() => {
    fetchOrgUnits(workspaceId ? { workspaceId } : undefined);
  }, [fetchOrgUnits, workspaceId]);

  useEffect(() => {
    const handleStoreEvent = (event: Event) => {
      const detail = (event as CustomEvent<{ store?: string; message?: string; severity?: string }>).detail;
      if (detail?.store !== 'enterpriseIdentity' || !detail.message) return;
      const type =
        event.type === 'store-success'
          ? 'success'
          : event.type === 'store-error'
            ? 'error'
            : 'warning';
      addNotification({
        type,
        title: type === 'success' ? 'Org hierarchy saved' : type === 'warning' ? 'Org hierarchy warning' : 'Org hierarchy error',
        message: detail.message,
        duration: type === 'success' ? 3000 : 6000,
      });
    };

    window.addEventListener('store-success', handleStoreEvent);
    window.addEventListener('store-warning', handleStoreEvent);
    window.addEventListener('store-error', handleStoreEvent);
    return () => {
      window.removeEventListener('store-success', handleStoreEvent);
      window.removeEventListener('store-warning', handleStoreEvent);
      window.removeEventListener('store-error', handleStoreEvent);
    };
  }, [addNotification]);
  
  // Convert Map to array and build tree
  const orgUnitsArray = useMemo(
    () =>
      Array.from(orgUnits.values()).filter((orgUnit) => {
        if (workspaceId == null) return true;
        return orgUnit.workspaceId === workspaceId || orgUnit.workspaceId == null;
      }),
    [orgUnits, workspaceId]
  );
  const orgTree = useMemo(() => buildOrgTree(orgUnitsArray), [orgUnitsArray]);
  
  // Count enabled queues
  const enabledQueuesCount = useMemo(
    () => orgUnitsArray.filter(u => u.queueSettings?.enabled).length,
    [orgUnitsArray]
  );
  
  const handleToggleExpand = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  
  const handleExpandAll = () => {
    setExpanded(new Set(orgUnitsArray.map(u => u.id)));
  };
  
  const handleCollapseAll = () => {
    setExpanded(new Set());
  };
  
  const handleSaveQueueSettings = async (settings: QueueSettings) => {
    if (!editingUnit) return;
    const queueId = editingUnit.id;

    const saved = await updateOrgUnit(queueId, { queueSettings: settings });
    if (!saved.ok) {
      addNotification({
        type: 'error',
        title: 'Could not save queue settings',
        message: saved.error ?? 'The server refused the change.',
      });
      return;
    }
    setEditingUnit(null);

    // "New and existing" backfills items already sitting in this queue. The
    // `.is('owner_id', null)` predicate is the whole guarantee that an item with
    // an owner is never touched — it is a filter, not a comparison done after
    // reading, so there is no window where a concurrent assignment gets clobbered.
    if (settings.defaultStewardId && settings.defaultOwnerApplyTo === 'new_and_existing') {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        const { data, error } = await supabase
          .from('items')
          .update({ owner_id: settings.defaultStewardId, updated_at: new Date().toISOString() })
          .eq('queue_id', queueId)
          .is('owner_id', null)
          .select('id');
        if (error) {
          addNotification({
            type: 'error',
            title: 'Could not assign the default owner',
            message: error.message,
          });
        } else {
          const n = data?.length ?? 0;
          addNotification({
            type: 'success',
            title: 'Default owner saved',
            message:
              n === 0
                ? 'Every item in this queue already has an owner — none were changed.'
                : `Assigned to ${n} item${n === 1 ? '' : 's'} that had no owner.`,
          });
          void fetchItems();
        }
      }
    }
  };

  const handleCreateOrgUnit = (unit: Omit<OrgUnit, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newId = addOrgUnit(unit);
    if (unit.parentId) {
      setExpanded((prev) => new Set(prev).add(unit.parentId!));
    }
    setExpanded((prev) => new Set(prev).add(newId));
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={<Inbox className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Queues"
        subtitle="Configure which organizational units act as queues for item submissions."
      />

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card">
          <div className="flex items-center">
            <Building2 className="h-6 w-6 text-[var(--text-ter)] mr-3" />
            <div>
              <p className="text-sm font-medium text-[var(--text-ter)]">Org Units</p>
              <p className="text-2xl font-semibold text-[var(--text-pri)]">{orgUnitsArray.length}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <Inbox className="h-6 w-6 text-[var(--green)] mr-3" />
            <div>
              <p className="text-sm font-medium text-[var(--text-ter)]">Active Queues</p>
              <p className="text-2xl font-semibold text-[var(--text-pri)]">{enabledQueuesCount}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <Users className="h-6 w-6 text-[var(--text-ter)] mr-3" />
            <div>
              <p className="text-sm font-medium text-[var(--text-ter)]">Teams</p>
              <p className="text-2xl font-semibold text-[var(--text-pri)]">
                {orgUnitsArray.filter(u => u.type === 'team').length}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Org Tree */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-[var(--text-pri)]">Organization Hierarchy</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setShowCreateOrgUnit(true)}
              className="btn btn-primary text-sm inline-flex items-center gap-2"
              disabled={!workspaceId}
            >
              <Plus className="h-4 w-4" />
              Add Org Unit
            </button>
            {orgUnitsArray.length === 0 && (
              <button
                onClick={loadDemoData}
                className="btn btn-secondary text-sm"
              >
                Load Demo Data
              </button>
            )}
            <button onClick={handleExpandAll} className="text-sm text-primary-600 hover:underline">
              Expand All
            </button>
            <button onClick={handleCollapseAll} className="text-sm text-primary-600 hover:underline">
              Collapse All
            </button>
          </div>
        </div>
        
        {orgUnitsArray.length === 0 ? (
          <div className="text-center py-12 text-[var(--text-ter)]">
            <AlertCircle className="h-12 w-12 mx-auto mb-3 text-[var(--text-ter)]" />
            <p className="text-lg font-medium">No organizational units configured</p>
            <p className="text-sm mt-1">Load demo data or configure your organization in Identity settings.</p>
          </div>
        ) : (
          <div className="space-y-1">
            {orgTree.map(node => (
              <OrgUnitRow
                key={node.id}
                node={node}
                depth={0}
                expanded={expanded}
                onToggleExpand={handleToggleExpand}
                onEditQueue={setEditingUnit}
                onOpenEmbed={setEmbedUnit}
                showIntakeEmbed={showIntakeEmbed}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Info Box */}
      <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4">
        <h3 className="font-medium text-[var(--text-sec)] mb-2">How Queues Work</h3>
        <ul className="text-sm text-[var(--text-sec)] space-y-1 list-disc list-inside">
          <li>Each organizational unit can be enabled as a queue</li>
          <li>Users submit ideas to a specific queue based on the topic</li>
          <li>Item Managers in each queue manage intake and structuring for their items</li>
          <li>Users only see items in queues they have permission to access</li>
        </ul>
      </div>
      
      {/* Queue Settings Modal */}
      {editingUnit && (
        <QueueSettingsEditor
          orgUnit={editingUnit}
          onSave={handleSaveQueueSettings}
          onClose={() => setEditingUnit(null)}
        />
      )}
      {showCreateOrgUnit && (
        <OrgUnitCreator
          workspaceId={workspaceId}
          orgUnits={orgUnitsArray}
          onCreate={handleCreateOrgUnit}
          onClose={() => setShowCreateOrgUnit(false)}
        />
      )}
      {/* Embed code modal */}
      {embedUnit && (
        <EmbedCodeModal orgUnit={embedUnit} onClose={() => setEmbedUnit(null)} />
      )}
    </div>
  );
}
